import type { BuyerPaymentMethod, PublicCheckoutFunnelNode } from './contract';
import type { CheckoutVoucher } from './api';
import { formatMoney, periodSuffix } from './money';
import { CardIcon, CheckIcon, LockIcon, PixIcon, ShieldIcon } from './icons';
import { UpsellMedia } from './UpsellMedia';
import { tr } from '../i18n';
import { CheckoutErrorAlert } from './CheckoutErrorAlert';
import { upsellAcceptLabel, upsellRejectLabel } from './upsellLabels';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import type { CheckoutSkin } from './usePublicCheckout';

export function UpsellStep({
  node,
  busy,
  error,
  voucher,
  demo,
  paymentMethod,
  skin = 'new',
  onAccept,
  onReject,
  onSkip,
  onErrorAction,
}: {
  node: PublicCheckoutFunnelNode;
  busy: boolean;
  error: CheckoutErrorDescriptor | null;
  voucher: CheckoutVoucher | null;
  demo?: boolean;
  paymentMethod: BuyerPaymentMethod;
  skin?: CheckoutSkin;
  onAccept: () => void;
  onReject: () => void;
  onSkip: () => void;
  onErrorAction?: () => void;
}) {
  const priceMoney = node.subscription?.value ?? node.price;
  const suffix = node.subscription
    ? periodSuffix(node.subscription.billingPeriodUnit, node.subscription.billingPeriodCount)
    : '';
  const priceLabel = `${formatMoney(priceMoney)}${suffix}`;
  const badge = node.kind === 'downsell' ? tr('upsell.downsellBadge') : tr('upsell.badge');
  const hasVoucher = Boolean(voucher?.pixCopyPaste || voucher?.pixQrCodeBase64);
  const trialDays = node.subscription?.trialDays ?? 0;
  const isPix = paymentMethod === 'pix';
  const isCard = paymentMethod === 'card';
  const acceptLabel = isPix
    ? tr('upsell.generatePix', { value: priceLabel })
    : upsellAcceptLabel(node, isCard ? priceLabel : undefined);

  if (hasVoucher) {
    return (
      <main className="vp-shell vp-shell--center">
        <section className="vp-card vp-success">
          {demo && <span className="vp-demo-badge">{tr('common.demo')}</span>}
          <div className="vp-success-mark" aria-hidden="true">
            <PixIcon width={36} height={36} />
          </div>
          <h1>{tr('upsell.pixTitle')}</h1>
          <p>{tr('upsell.pixBody')}</p>
          {voucher?.pixQrCodeBase64 && (
            <img className="vp-pix-qr" alt="QR Code Pix" src={`data:image/png;base64,${voucher.pixQrCodeBase64}`} />
          )}
          {voucher?.pixCopyPaste && (
            <label className="vp-field">
              <span>{tr('pix.copyPaste')}</span>
              <input readOnly value={voucher.pixCopyPaste} aria-label={tr('pix.copyPaste')} />
            </label>
          )}
          <div className="vp-success-summary">
            <span>{node.title}</span>
            <strong>{priceLabel}</strong>
          </div>
          <button type="button" className="vp-link" onClick={onSkip} disabled={busy}>
            {tr('upsell.continueWithout')}
          </button>
        </section>
      </main>
    );
  }

  if (skin === 'legacy') {
    return (
      <main className="vp-shell vp-shell--center">
        <section className="vp-upsell-legacy">
          {demo && <span className="vp-demo-badge">{tr('common.demo')}</span>}
          <div className="vp-upsell-legacy-offer">
            <span className="vp-upsell-legacy-badge">{badge}</span>
            <strong>{node.title}</strong>
            {node.subtext && <p>{node.subtext}</p>}
            <div className="vp-upsell-legacy-summary">
              <span className="vp-upsell-legacy-price vp-num">{priceLabel}</span>
              {trialDays > 0 && (
                <span className="vp-upsell-legacy-trial">
                  {tr('upsell.trialDays', { days: trialDays })}
                  {node.subscription?.trialPrice
                    ? ` · ${formatMoney(node.subscription.trialPrice)}`
                    : ` ${tr('common.free')}`}
                </span>
              )}
            </div>
          </div>

          {error && (
            <CheckoutErrorAlert
              error={error}
              placement="toast"
              {...(onErrorAction !== undefined ? { onAction: onErrorAction } : {})}
            />
          )}

          <button
            type="button"
            className="vp-upsell-legacy-accept"
            onClick={onAccept}
            disabled={busy}
          >
            {busy ? tr('common.processing') : acceptLabel}
          </button>
          <button
            type="button"
            className="vp-upsell-legacy-reject"
            onClick={onReject}
            disabled={busy}
          >
            {upsellRejectLabel(node)}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="vp-shell vp-shell--center">
      <section className="vp-card vp-upsell">
        {demo && <span className="vp-demo-badge">{tr('common.demo')}</span>}
        <div className="vp-upsell-reassurance">
          <CheckIcon width={16} height={16} />
          <span>{tr('upsell.reassurance')}</span>
        </div>
        <span className="vp-upsell-badge">{badge}</span>
        {node.mode === 'hosted' && node.mediaUrl && <UpsellMedia src={node.mediaUrl} />}
        <h1 className="vp-upsell-title">{node.title}</h1>
        {node.subtext && <p className="vp-upsell-subtext">{node.subtext}</p>}
        <div className="vp-upsell-price">
          <strong className="vp-num">{priceLabel}</strong>
          {trialDays > 0 && (
            <span className="vp-faint vp-small">
              {tr('upsell.trialDays', { days: trialDays })}
              {node.subscription?.trialPrice ? ` · ${formatMoney(node.subscription.trialPrice)}` : ` ${tr('common.free')}`}
            </span>
          )}
        </div>

        {error && (
          <CheckoutErrorAlert
            error={error}
            placement="toast"
            {...(onErrorAction !== undefined ? { onAction: onErrorAction } : {})}
          />
        )}

        <button type="button" className="vp-cta" onClick={onAccept} disabled={busy}>
          {busy ? (
            <>
              <span className="vp-spinner vp-spinner--sm" aria-hidden="true" />
              {tr('common.processing')}
            </>
          ) : (
            <>
              <CheckIcon width={18} height={18} />
              {acceptLabel}
            </>
          )}
        </button>

        <button type="button" className="vp-upsell-decline vp-link" onClick={onReject} disabled={busy}>
          {upsellRejectLabel(node)}
        </button>

        <div className="vp-trust">
          {isPix ? (
            <>
              <span>
                <PixIcon width={15} height={15} /> {tr('upsell.pixSecure')}
              </span>
              <span>
                <ShieldIcon width={15} height={15} /> {tr('upsell.pixOnlyOnConfirm')}
              </span>
            </>
          ) : (
            <>
              <span>
                <LockIcon width={15} height={15} /> {tr('upsell.oneClick')}
              </span>
              <span>
                <CardIcon width={15} height={15} /> {tr('upsell.noCardAgain')}
              </span>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
